import { useMemo } from 'react';
import { QueryFunctionContext } from '@tanstack/react-query';
import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  Page,
} from '../../common/queryClient';
import { ClassifierUrl } from './ClassifierUrl';
import { CategorySelectType } from '../types';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';
import { formatCodeName } from '../../common/utils';

export interface SearchCategoriesQueryParams {
  searchText: string;
  classifierId?: number;
  classifierCode?: string;
  page?: number;
  size?: number;
}

export const searchCategories = ({
  searchText,
  classifierId,
  classifierCode,
  page,
  size,
}: SearchCategoriesQueryParams) =>
  apiClient
    .get<Page<CategorySelectType>>(
      expand(ClassifierUrl.SEARCH_CATEGORIES, {
        searchText,
        classifierId,
        classifierCode,
        page,
        size,
      })
    )
    .then((response) => response.data);

const createSearchCategories = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<OptionsPage> => {
  const page = pageParam ?? 0;
  const params = meta as Partial<SearchCategoriesQueryParams> | undefined;
  const result = await searchCategories({
    ...params,
    searchText: (meta?.searchText ?? '') as string,
    page,
    size: AUTOCOMPLETE_PAGE_SIZE,
  });

  const content: OptionType[] = result.content.map((item) => {
    return {
      value: item.code,
      label: formatCodeName(item),
    };
  });
  return {
    content,
    last: result.last,
    number: result.number,
  };
};

export function useInfiniteSearchCategoriesQuery(
  term: string,
  classifierId?: number,
  classifierCode?: string
) {
  const options = useMemo(
    () => ({
      classifierId,
      classifierCode,
    }),
    [classifierId, classifierCode]
  );
  return useInfiniteSearchQuery(
    ['search-categories-autocomplete'],
    createSearchCategories,
    term,
    options
  );
}
